package com.shreyasjoshi.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import com.shreyasjoshi.models.PortfolioData

@Composable
fun HeroSection(
    isDarkMode: Boolean,
    onOpenResumeModal: () -> Unit
) {
    val info = PortfolioData.personalInfo

    Section(
        attrs = {
            id("hero")
            classNames(
                "portfolio-section",
                "relative", "py-20", "px-4", "sm:px-6", "lg:px-8", "max-w-6xl", "mx-auto", "border-b", "transition-colors",
                if (isDarkMode) "border-white/10" else "border-slate-200"
            )
        }
    ) {
        Div(attrs = { classNames("flex", "flex-col", "gap-8") }) {
            // Status and Specialization Eyebrow
            Div(attrs = { classNames("flex", "flex-wrap", "items-center", "gap-3") }) {
                Span(
                    attrs = {
                        classNames(
                            "px-2.5", "py-1", "rounded-sm", "text-[11px]", "font-mono", "font-semibold", "tracking-wider", "uppercase",
                            if (isDarkMode) "bg-[#189AD3]/10 text-[#71C7EC] border border-[#189AD3]/30"
                            else "bg-sky-50 text-sky-800 border border-sky-200"
                        )
                    }
                ) {
                    Text(info.specialization)
                }

                Span(
                    attrs = {
                        classNames(
                            "px-2.5", "py-1", "rounded-sm", "text-[11px]", "font-mono", "flex", "items-center", "gap-1.5",
                            if (isDarkMode) "bg-white/5 text-white/70 border border-white/10"
                            else "bg-slate-100 text-slate-700 border border-slate-200"
                        )
                    }
                ) {
                    Span(attrs = { classNames("w-2", "h-2", "rounded-full", "bg-emerald-400", "animate-pulse") })
                    Text("Renault Group • Bengaluru")
                }
            }

            // Headline & Name
            Div(attrs = { classNames("space-y-3") }) {
                H1(
                    attrs = {
                        classNames(
                            "text-4xl", "sm:text-6xl", "font-extrabold", "tracking-tight",
                            if (isDarkMode) "text-white" else "text-slate-900"
                        )
                    }
                ) {
                    Text(info.name)
                }

                H2(
                    attrs = {
                        classNames(
                            "text-xl", "sm:text-2xl", "font-semibold",
                            if (isDarkMode) "text-white/70" else "text-slate-600"
                        )
                    }
                ) {
                    Text("${info.headline} — ${info.androidExperience} Android Engineering (${info.totalExperience} Total IT)")
                }
            }

            // Bio Summary
            P(
                attrs = {
                    classNames(
                        "text-base", "sm:text-lg", "leading-relaxed", "max-w-3xl",
                        if (isDarkMode) "text-white/60" else "text-slate-600"
                    )
                }
            ) {
                Text(info.summaryParagraphs[0])
            }

            // Key Quick Stats Matrix
            Div(attrs = { classNames("hero-stats", "grid", "grid-cols-2", "sm:grid-cols-4", "gap-3", "pt-2") }) {
                StatCard(
                    title = "Android Exp",
                    value = info.androidExperience,
                    caption = "AAOS & TV Focus",
                    isDarkMode = isDarkMode
                )
                StatCard(
                    title = "Total IT",
                    value = info.totalExperience,
                    caption = "Troubleshooting & Systems",
                    isDarkMode = isDarkMode
                )
                StatCard(
                    title = "AOSP Services",
                    value = "CarSystemUI",
                    caption = "CarLauncher & Cuttlefish",
                    isDarkMode = isDarkMode
                )
                StatCard(
                    title = "Architecture",
                    value = "ASPICE",
                    caption = "SWE.1/SWE.2 & UML",
                    isDarkMode = isDarkMode
                )
            }

            // Primary Call to Action Buttons
            Div(attrs = { classNames("hero-actions", "flex", "flex-wrap", "items-center", "gap-4", "pt-4") }) {
                A(
                    href = "#projects",
                    attrs = {
                        classNames(
                            "px-5", "py-2.5", "rounded-sm", "bg-[#189AD3]", "hover:bg-[#107DAC]", "text-white",
                            "font-mono", "text-xs", "font-semibold", "tracking-wider", "transition-all", "shadow-sm"
                        )
                    }
                ) {
                    Text("EXPLORE PROJECTS")
                }

                Button(
                    attrs = {
                        classNames(
                            "px-5", "py-2.5", "rounded-sm", "border", "font-mono", "text-xs", "font-semibold",
                            "tracking-wider", "transition-all",
                            if (isDarkMode) "border-white/20 text-white/90 hover:border-white/50 bg-white/5"
                            else "border-slate-300 text-slate-800 hover:border-slate-500 bg-slate-100"
                        )
                        onClick { onOpenResumeModal() }
                    }
                ) {
                    Text("RESUME PREVIEW")
                }

                A(
                    href = info.knowledgeBase,
                    attrs = {
                        classNames(
                            "px-4", "py-2.5", "rounded-sm", "font-mono", "text-xs", "text-[#189AD3]",
                            "hover:underline", "flex", "items-center", "gap-1.5"
                        )
                    }
                ) {
                    Text("Technical Knowledge Base ↗")
                }
            }
        }
    }
}

@Composable
private fun StatCard(
    title: String,
    value: String,
    caption: String,
    isDarkMode: Boolean
) {
    Div(
        attrs = {
            classNames(
                "p-3.5", "rounded-sm", "border", "transition-colors",
                if (isDarkMode) "bg-[#0a0a0a] border-white/10" else "bg-slate-50 border-slate-200"
            )
        }
    ) {
        Div(
            attrs = {
                classNames(
                    "text-[10px]", "font-mono", "uppercase", "tracking-wider",
                    if (isDarkMode) "text-white/40" else "text-slate-500"
                )
            }
        ) {
            Text(title)
        }
        Div(
            attrs = {
                classNames(
                    "text-lg", "font-bold", "mt-0.5",
                    if (isDarkMode) "text-white" else "text-slate-900"
                )
            }
        ) {
            Text(value)
        }
        Div(
            attrs = {
                classNames(
                    "text-[11px]", "mt-0.5",
                    if (isDarkMode) "text-white/50" else "text-slate-500"
                )
            }
        ) {
            Text(caption)
        }
    }
}


